import { inject } from '@angular/core';
import { CanMatchFn } from '@angular/router';
import { AUTHENTICATION_SERVICE } from '@processpuzzle/auth';

/** The realm role org-admin-backend's `requireAdministration` asks for, beside membership. */
export const ORG_ADMIN_ROLE = 'org-admin';

/**
 * Lets the administration branch match only for somebody who may administer, so that a plain member
 * reaches `AdministrationDeniedComponent` instead of lists the backend answers with a 403 — which the
 * screens showed as empty, as if the organization had no users and no roles.
 *
 * **`canMatch`, so the refusal keeps the URL.** The denied route is a sibling with the same path, matched
 * when this one is not; a redirect would put an address in the browser that the user never asked for.
 *
 * **It authenticates first.** Matching precedes `canActivate`, so on a page opened cold the session does
 * not exist yet when this runs. Without one it matches anyway and leaves the login to the branch's
 * `loginRequiredGuard`, which brings the user back here with a token to decide on.
 *
 * Membership is not checked: this application is bootstrapped for one organization and its token asks
 * for that organization's scope. Neither is this the security decision — the backend makes that on every
 * request. It is the same question asked early, to say no in words.
 */
export const orgAdminGuard: CanMatchFn = async () => {
  const authService = inject(AUTHENTICATION_SERVICE);
  if (!(await authService.authenticate())) return true;
  return authService.getCurrentUser()?.hasRole(ORG_ADMIN_ROLE) ?? false;
};
